import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { useQueryClient } from '@tanstack/react-query';
import Cookie from 'js-cookie';
import { useCallback } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'shared-routing';

import { Routes } from '@common/constants';
import { invalidateSpaPackageTimeslots } from '@features/spa';
import { createBasket, updateBasket } from '@store/basket/actions';
import { basketSelectors } from '@store/basket/selectors';
import { SpaPackageType } from 'shared-types/SpaPackage';

import { SpaPackageFormValues } from '../../SpaPackageForm.schema';
import {
  SpaPackageBasketResolveType,
  SpaPackageFormDataType,
} from '../../SpaPackageModal/SpaPackageModal.types';
import { parseSpaPackageData } from '../../SpaPackageModal/SubmitButton/SubmitButton.helpers';

type UseSubmitBookingParams = {
  form: UseFormReturn<SpaPackageFormValues>;
  entity: SpaPackageType | undefined;
  propertyId: number;
  language: string;
  id: number;
  onClose: (showMiniBasket: boolean) => void;
};

export function useSubmitBooking({
  form,
  entity,
  propertyId,
  language,
  id,
  onClose,
}: UseSubmitBookingParams) {
  const dispatch = useDispatch();
  const router = useRouter();
  const queryClient = useQueryClient();
  const isGroupedSpaBookingEnabled = useFeatureIsOn('spa-days-grouped-booking');
  const currentBasketItems = useSelector(
    (state: Record<string, unknown>) =>
      (basketSelectors.getBasketDetails(state) as { items?: unknown[] })?.items || []
  );

  const addToBasket = useCallback(
    (data: SpaPackageFormValues) => {
      if (!entity) return;
      const cleanedExtras = Object.fromEntries(
        Object.entries(data.extras || {}).filter(([, v]) => Array.isArray(v) && v.length > 0)
      );
      const cleanedData = { ...data, extras: cleanedExtras };
      const parsedBasketItems = parseSpaPackageData(
        cleanedData as unknown as SpaPackageFormDataType,
        entity,
        isGroupedSpaBookingEnabled
      );
      const promises: SpaPackageBasketResolveType[] = [];
      if (currentBasketItems.length) {
        const basketId = Cookie.get('basketId') as string;
        const items = [...currentBasketItems, ...parsedBasketItems];
        promises.push(
          dispatch(
            updateBasket(propertyId, basketId, language, { items }, false, true) as any
          ) as unknown as SpaPackageBasketResolveType
        );
      } else {
        promises.push(
          dispatch(
            createBasket(propertyId, language, { items: parsedBasketItems }, true) as any
          ) as unknown as SpaPackageBasketResolveType
        );
      }
      Promise.any(promises)
        .then(() => {
          invalidateSpaPackageTimeslots(queryClient);
          onClose(true);
        })
        .catch(() => {
          router.push({ pathname: Routes.SPA_DAY_DETAIL, query: { id } });
        });
    },
    [
      entity,
      isGroupedSpaBookingEnabled,
      currentBasketItems,
      dispatch,
      propertyId,
      language,
      queryClient,
      onClose,
    ]
  );

  const handleSubmit = form.handleSubmit(addToBasket);

  const handleNextStep = form.handleSubmit(() => {
    router.push({
      pathname: Routes.SPA_DAY_BOOKING,
      query: { id, bookingStep: 'extras' },
    });
  });

  const handleBack = useCallback(() => {
    router.push({ pathname: `${Routes.SPA_DAY_DETAIL.replace('[id]', String(id))}` });
  }, [router, id]);

  return {
    handleSubmit,
    handleNextStep,
    handleBack,
  };
}
